import React from "react";
import { classNames, formatDuration, formatRelative } from "./utils";

function Metric({ label, value, sublabel, tone = "default" }) {
  const toneMap = {
    default: "bg-white ring-zinc-200",
    indigo: "bg-white ring-indigo-200",
    amber: "bg-amber-50 ring-amber-200",
    red: "bg-red-50 ring-red-200",
    emerald: "bg-emerald-50 ring-emerald-200",
  };
  return (
    <div
      className={classNames(
        "flex min-w-[140px] flex-1 flex-col gap-0.5 rounded-xl px-3 py-2 ring-1",
        toneMap[tone] || toneMap.default
      )}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
        {label}
      </span>
      <span className="text-lg font-semibold leading-tight text-zinc-900">{value}</span>
      {sublabel ? (
        <span className="text-[11px] leading-4 text-zinc-500">{sublabel}</span>
      ) : null}
    </div>
  );
}

function UrgencyBar({ buckets, total }) {
  const ordered = [
    { id: "overdue", label: ">1d", tone: "bg-red-500", count: buckets.overdue || 0 },
    { id: "stale", label: "4h–1d", tone: "bg-amber-500", count: buckets.stale || 0 },
    { id: "fresh", label: "1–4h", tone: "bg-indigo-500", count: buckets.fresh || 0 },
    { id: "new", label: "<1h", tone: "bg-emerald-500", count: buckets.new || 0 },
  ];
  const safeTotal = total || ordered.reduce((sum, b) => sum + b.count, 0) || 1;
  return (
    <div className="flex min-w-[240px] flex-1 flex-col rounded-xl bg-white px-3 py-2 ring-1 ring-zinc-200">
      <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
        <span>Unreplied by age</span>
        <span className="text-zinc-400">{ordered.reduce((s, b) => s + b.count, 0)}</span>
      </div>
      <div className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-zinc-100">
        {ordered.map((b) => (
          <div
            key={b.id}
            className={classNames("h-full", b.tone)}
            style={{ width: `${(b.count / safeTotal) * 100}%` }}
            title={`${b.label}: ${b.count}`}
          />
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-zinc-600">
        {ordered.map((b) => (
          <span key={b.id} className="inline-flex items-center gap-1">
            <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", b.tone)} />
            {b.label} · <span className="font-semibold text-zinc-800">{b.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function MetricStrip({ analytics }) {
  if (!analytics) return null;

  const deltaLabel =
    analytics.newTodayDelta > 0
      ? `+${analytics.newTodayDelta} vs yesterday`
      : analytics.newTodayDelta < 0
      ? `${analytics.newTodayDelta} vs yesterday`
      : "same as yesterday";

  const avg = analytics.avgFirstResponseMinutes;

  return (
    <div className="flex flex-wrap items-stretch gap-2">
      <Metric
        label="Open inquiries"
        value={analytics.totalOpen}
        sublabel={`${analytics.openPct}% of total`}
        tone="indigo"
      />
      <Metric
        label="New today"
        value={analytics.newToday}
        sublabel={deltaLabel}
        tone={analytics.newTodayDelta > 0 ? "emerald" : "default"}
      />
      <Metric
        label="Awaiting reply"
        value={analytics.byStatus?.new || 0}
        sublabel={
          analytics.oldestUnreplied
            ? `Oldest ${formatRelative(analytics.oldestUnreplied.createdAt)}`
            : "All caught up"
        }
        tone={(analytics.buckets?.overdue || 0) > 0 ? "red" : "default"}
      />
      <Metric
        label="Avg 1st response"
        value={avg != null ? formatDuration(avg) : "—"}
        sublabel="last 30 days"
      />
      <Metric
        label="Replies sent"
        value={analytics.repliesLast7d}
        sublabel="last 7 days"
      />
      <UrgencyBar buckets={analytics.buckets || {}} total={analytics.byStatus?.new || 0} />
    </div>
  );
}
